import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType } from '../../api/errorHandling';
import {
 AccessGroupsDTO,
 CreateUpdateUsersDTO,
 FilterDTO,
 GroupDTO,
 JWTRefreshTokenSubmitDTO,
 JWTTokenDTO,
 LoginTwoFAVerifyDTO,
 RolesDTO,
 TwoFAVerifyDTO,
 UsersDTO,
 VerifyDTO,
} from './identityTypes';
import {
 commonReducers,
 CommonState,
 initialCommonState,
} from '../_common/commonReducers';
import { ToastMessageProps } from '../toast/toastSlice';

interface IdentityState extends CommonState {
 roles?: RolesDTO[];
 role?: RolesDTO | null;
 groups?: GroupDTO[];
 group?: GroupDTO | null;
 accessGroups?: AccessGroupsDTO[];
 qrCode?: { uri: string } | null;
 error: { [key: string]: ErrorType | null };
 login?: JWTTokenDTO | null;
 uri?: string | null;
 apiKey?: string | null;
}

export const initialState: IdentityState = {
 ...initialCommonState,
 login: null,
 roles: undefined,
 role: null,
 groups: undefined,
 group: null,
 accessGroups: undefined,
 qrCode: null,
 error: {},
 uri: null,
 apiKey: null,
};

const identitySlice = createSlice({
 name: 'identity',
 initialState,
 reducers: {
  ...commonReducers,
  identityLoginStart(
   state,
   action: PayloadAction<{ email: string; password: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityLogin: true },
   };
  },
  identityLoginSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityLogin: true },
    loading: { ...state.loading, identityLogin: null },
    error: { ...(state.error || {}), identityLogin: null },
   };
  },
  identityLoginFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityLogin: null },
    loading: { ...state.loading, identityLogin: null },
    error: { ...(state.error || {}), identityLogin: action.payload },
   };
  },
  identityLogoutStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityLogout: true },
   };
  },
  identityLogoutSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, identityLogout: true },
    loading: { ...state.loading, identityLogout: null },
   };
  },
  identityLogoutFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityLogout: null },
    loading: { ...state.loading, identityLogout: null },
    error: { ...(state.error || {}), identityLogout: action.payload },
   };
  },
  identityRefreshTokenStart(
   state,
   action: PayloadAction<JWTRefreshTokenSubmitDTO>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityRefreshToken: true },
   };
  },
  identityRefreshTokenSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityRefreshToken: true },
    loading: { ...state.loading, identityRefreshToken: null },
    error: { ...(state.error || {}), identityRefreshToken: null },
   };
  },
  identityRefreshTokenFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRefreshToken: null },
    loading: { ...state.loading, identityRefreshToken: null },
    error: { ...(state.error || {}), identityRefreshToken: action.payload },
   };
  },
  identityMicrosoftStart(
   state,
   action: PayloadAction<{ redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityMicrosoft: true },
   };
  },
  identityMicrosoftSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    uri: action.payload.uri,
    success: { ...state.success, identityMicrosoft: true },
    loading: { ...state.loading, identityMicrosoft: null },
    error: { ...(state.error || {}), identityMicrosoft: null },
   };
  },
  identityMicrosoftFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityMicrosoft: null },
    loading: { ...state.loading, identityMicrosoft: null },
    error: { ...(state.error || {}), identityMicrosoft: action.payload },
   };
  },
  identityMicrosoftCallbackStart(
   state,
   action: PayloadAction<{ code?: string; redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityMicrosoftCallback: true },
   };
  },
  identityMicrosoftCallbackSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityMicrosoftCallback: true },
    loading: { ...state.loading, identityMicrosoftCallback: null },
    error: { ...(state.error || {}), identityMicrosoftCallback: null },
   };
  },
  identityMicrosoftCallbackFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityMicrosoftCallback: null },
    loading: { ...state.loading, identityMicrosoftCallback: null },
    error: {
     ...(state.error || {}),
     identityMicrosoftCallback: action.payload,
    },
   };
  },
  identityGoogleStart(state, action: PayloadAction<{ redirectUri?: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityGoogle: true },
   };
  },
  identityGoogleSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    uri: action.payload.uri,
    success: { ...state.success, identityGoogle: true },
    loading: { ...state.loading, identityGoogle: null },
    error: { ...(state.error || {}), identityGoogle: null },
   };
  },
  identityGoogleFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGoogle: null },
    loading: { ...state.loading, identityGoogle: null },
    error: { ...(state.error || {}), identityGoogle: action.payload },
   };
  },
  identityGoogleCallbackStart(
   state,
   action: PayloadAction<{ code?: string; redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGoogleCallback: true },
   };
  },
  identityGoogleCallbackSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityGoogleCallback: true },
    loading: { ...state.loading, identityGoogleCallback: null },
    error: { ...(state.error || {}), identityGoogleCallback: null },
   };
  },
  identityGoogleCallbackFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGoogleCallback: null },
    loading: { ...state.loading, identityGoogleCallback: null },
    error: { ...(state.error || {}), identityGoogleCallback: action.payload },
   };
  },
  identityVerify2FAStart(state, action: PayloadAction<LoginTwoFAVerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityVerify2FA: true },
   };
  },
  identityVerify2FASuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityVerify2FA: true },
    loading: { ...state.loading, identityVerify2FA: null },
    error: { ...(state.error || {}), identityVerify2FA: null },
   };
  },
  identityVerify2FAFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityVerify2FA: null },
    loading: { ...state.loading, identityVerify2FA: null },
    error: { ...(state.error || {}), identityVerify2FA: action.payload },
   };
  },
  identityGenerate2FACodeStart(state, action: PayloadAction<TwoFAVerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityGenerate2FACode: true },
   };
  },
  identityGenerate2FACodeSuccess(
   state,
   action: PayloadAction<{ uri: string }>,
  ) {
   console.log(action.payload);
   return {
    ...state,
    qrCode: { uri: action.payload.uri },
    success: { ...state.success, identityGenerate2FACode: true },
    loading: { ...state.loading, identityGenerate2FACode: null },
    error: { ...(state.error || {}), identityGenerate2FACode: null },
   };
  },
  identityGenerate2FACodeFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGenerate2FACode: null },
    loading: { ...state.loading, identityGenerate2FACode: null },
    error: { ...(state.error || {}), identityGenerate2FACode: action.payload },
   };
  },
  identitySetVerificationStart(state, action: PayloadAction<VerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identitySetVerification: true },
   };
  },
  identitySetVerificationSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identitySetVerification: true },
    loading: { ...state.loading, identitySetVerification: null },
    error: { ...(state.error || {}), identitySetVerification: null },
   };
  },
  identitySetVerificationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identitySetVerification: null },
    loading: { ...state.loading, identitySetVerification: null },
    error: { ...(state.error || {}), identitySetVerification: action.payload },
   };
  },
  identityConfirmVerificationStart(
   state,
   action: PayloadAction<{
    sessionId?: string;
    userid?: string;
    method?: 1 | 2 | 3;
    code?: string;
   }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityConfirmVerification: true },
   };
  },
  identityConfirmVerificationSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityConfirmVerification: true },
    loading: { ...state.loading, identityConfirmVerification: null },
    error: { ...(state.error || {}), identityConfirmVerification: null },
   };
  },
  identityConfirmVerificationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityConfirmVerification: null },
    loading: { ...state.loading, identityConfirmVerification: null },
    error: {
     ...(state.error || {}),
     identityConfirmVerification: action.payload,
    },
   };
  },
  identitySetTotpStart(
   state,
   action: PayloadAction<{ userid?: string; sessionId?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identitySetTotp: true },
   };
  },
  identitySetTotpSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    qrCode: { uri: action.payload.uri },
    success: { ...state.success, identitySetTotp: true },
    loading: { ...state.loading, identitySetTotp: null },
    error: { ...(state.error || {}), identitySetTotp: null },
   };
  },
  identitySetTotpFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identitySetTotp: null },
    loading: { ...state.loading, identitySetTotp: null },
    error: { ...(state.error || {}), identitySetTotp: action.payload },
   };
  },
  identityVerifyTotpStart(
   state,
   action: PayloadAction<{
    userid?: string;
    sessionId?: string;
    code?: string;
   }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityVerifyTotp: true },
   };
  },
  identityVerifyTotpSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityVerifyTotp: true },
    loading: { ...state.loading, identityVerifyTotp: null },
    error: { ...(state.error || {}), identityVerifyTotp: null },
   };
  },
  identityVerifyTotpFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityVerifyTotp: null },
    loading: { ...state.loading, identityVerifyTotp: null },
    error: { ...(state.error || {}), identityVerifyTotp: action.payload },
   };
  },
  identityGetSelfStart(state, action: PayloadAction<{ fields?: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityGetSelf: true },
   };
  },
  identityGetSelfSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    item: action.payload,
    success: { ...state.success, identityGetSelf: true },
    loading: { ...state.loading, identityGetSelf: null },
    error: { ...(state.error || {}), identityGetSelf: null },
   };
  },
  identityGetSelfFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetSelf: null },
    loading: { ...state.loading, identityGetSelf: null },
    error: { ...(state.error || {}), identityGetSelf: action.payload },
   };
  },
  identityGetSelfFinish(state) {
   return {
    ...state,
    loading: { ...state.loading, identityGetSelf: null },
    success: { ...state.success, identityGetSelf: null },
    error: { ...(state.error || {}), identityGetSelf: null },
   };
  },
  identityUpdateSelfStart(state, action: PayloadAction<CreateUpdateUsersDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityUpdateSelf: true },
   };
  },
  identityUpdateSelfSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    item: action.payload,
    success: { ...state.success, identityUpdateSelf: true },
    loading: { ...state.loading, identityUpdateSelf: null },
    error: { ...(state.error || {}), identityUpdateSelf: null },
   };
  },
  identityUpdateSelfFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityUpdateSelf: null },
    loading: { ...state.loading, identityUpdateSelf: null },
    error: { ...(state.error || {}), identityUpdateSelf: action.payload },
   };
  },
  identityUpdateSelfFinish(state) {
   return {
    ...state,
    loading: { ...state.loading, identityUpdateSelf: null },
    success: { ...state.success, identityUpdateSelf: null },
    error: { ...(state.error || {}), identityUpdateSelf: null },
   };
  },
  identityGetRolesStart(state, action: PayloadAction<FilterDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityGetRoles: true },
   };
  },
  identityGetRolesSuccess(state, action: PayloadAction<RolesDTO[]>) {
   return {
    ...state,
    roles: action.payload,
    success: { ...state.success, identityGetRoles: true },
    loading: { ...state.loading, identityGetRoles: null },
    error: { ...(state.error || {}), identityGetRoles: null },
   };
  },
  identityGetRolesFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetRoles: null },
    loading: { ...state.loading, identityGetRoles: null },
    error: { ...(state.error || {}), identityGetRoles: action.payload },
   };
  },
  identityGetItemRolesStart(state, action: PayloadAction<{ userId: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityGetItemRoles: true },
   };
  },
  identityGetItemRolesSuccess(state, action: PayloadAction<AccessGroupsDTO[]>) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, identityGetItemRoles: true },
    loading: { ...state.loading, identityGetItemRoles: null },
    error: { ...(state.error || {}), identityGetItemRoles: null },
   };
  },
  identityGetItemRolesFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetItemRoles: null },
    loading: { ...state.loading, identityGetItemRoles: null },
    error: { ...(state.error || {}), identityGetItemRoles: action.payload },
   };
  },
  identityGetItemGroupRolesStart(
   state,
   action: PayloadAction<{ userId: string; groupId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGetItemGroupRoles: true },
   };
  },
  identityGetItemGroupRolesSuccess(state, action: PayloadAction<RolesDTO[]>) {
   return {
    ...state,
    roles: action.payload,
    success: { ...state.success, identityGetItemGroupRoles: true },
    loading: { ...state.loading, identityGetItemGroupRoles: null },
    error: { ...(state.error || {}), identityGetItemGroupRoles: null },
   };
  },
  identityGetItemGroupRolesFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetItemGroupRoles: null },
    loading: { ...state.loading, identityGetItemGroupRoles: null },
    error: {
     ...(state.error || {}),
     identityGetItemGroupRoles: action.payload,
    },
   };
  },
  identityAddRoleToItemGroupStart(
   state,
   action: PayloadAction<{ userId: string; groupId: string; role: RolesDTO }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityAddRoleToItemGroup: true },
   };
  },
  identityAddRoleToItemGroupSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityAddRoleToItemGroup: true },
    loading: { ...state.loading, identityAddRoleToItemGroup: null },
    error: { ...(state.error || {}), identityAddRoleToItemGroup: null },
   };
  },
  identityAddRoleToItemGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityAddRoleToItemGroup: null },
    loading: { ...state.loading, identityAddRoleToItemGroup: null },
    error: {
     ...(state.error || {}),
     identityAddRoleToItemGroup: action.payload,
    },
   };
  },
  identityCreateRoleStart(state, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityCreateRole: true },
   };
  },
  identityCreateRoleSuccess(state, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    role: action.payload,
    success: { ...state.success, identityCreateRole: true },
    loading: { ...state.loading, identityCreateRole: null },
    error: { ...(state.error || {}), identityCreateRole: null },
   };
  },
  identityCreateRoleFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityCreateRole: null },
    loading: { ...state.loading, identityCreateRole: null },
    error: { ...(state.error || {}), identityCreateRole: action.payload },
   };
  },
  identityCreateAccessGroupStart(
   state,
   action: PayloadAction<AccessGroupsDTO>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityCreateAccessGroup: true },
   };
  },
  identityCreateAccessGroupSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO>,
  ) {
   return {
    ...state,
    accessGroups: state.accessGroups
     ? [...state.accessGroups, action.payload]
     : [action.payload],
    success: { ...state.success, identityCreateAccessGroup: true },
    loading: { ...state.loading, identityCreateAccessGroup: null },
    error: { ...(state.error || {}), identityCreateAccessGroup: null },
   };
  },
  identityCreateAccessGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityCreateAccessGroup: null },
    loading: { ...state.loading, identityCreateAccessGroup: null },
    error: {
     ...(state.error || {}),
     identityCreateAccessGroup: action.payload,
    },
   };
  },
  identityDeleteRoleStart(state, action: PayloadAction<RolesDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityDeleteRole: true },
   };
  },
  identityDeleteRoleSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityDeleteRole: true },
    loading: { ...state.loading, identityDeleteRole: null },
    error: { ...(state.error || {}), identityDeleteRole: null },
   };
  },
  identityDeleteRoleFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityDeleteRole: null },
    loading: { ...state.loading, identityDeleteRole: null },
    error: { ...(state.error || {}), identityDeleteRole: action.payload },
   };
  },
  identityRemoveRoleFromGroupStart(
   state,
   action: PayloadAction<{ groupId: string; role: RolesDTO }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityRemoveRoleFromGroup: true },
   };
  },
  identityRemoveRoleFromGroupSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityRemoveRoleFromGroup: true },
    loading: { ...state.loading, identityRemoveRoleFromGroup: null },
    error: { ...(state.error || {}), identityRemoveRoleFromGroup: null },
   };
  },
  identityRemoveRoleFromGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRemoveRoleFromGroup: null },
    loading: { ...state.loading, identityRemoveRoleFromGroup: null },
    error: {
     ...(state.error || {}),
     identityRemoveRoleFromGroup: action.payload,
    },
   };
  },
  identityRevokeTokenStart(state, action: PayloadAction<{ Id: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityRevokeToken: true },
   };
  },
  identityRevokeTokenSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityRevokeToken: true },
    loading: { ...state.loading, identityRevokeToken: null },
    error: { ...(state.error || {}), identityRevokeToken: null },
   };
  },
  identityRevokeTokenFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRevokeToken: null },
    loading: { ...state.loading, identityRevokeToken: null },
    error: { ...(state.error || {}), identityRevokeToken: action.payload },
   };
  },
  identityRevokeAllTokensStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityRevokeAllTokens: true },
   };
  },
  identityRevokeAllTokensSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityRevokeAllTokens: true },
    loading: { ...state.loading, identityRevokeAllTokens: null },
    error: { ...(state.error || {}), identityRevokeAllTokens: null },
   };
  },
  identityRevokeAllTokensFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRevokeAllTokens: null },
    loading: { ...state.loading, identityRevokeAllTokens: null },
    error: { ...(state.error || {}), identityRevokeAllTokens: action.payload },
   };
  },
  identityGetSelfGroupAccessStart(
   state,
   action: PayloadAction<{ groupId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGetSelfGroupAccess: true },
   };
  },
  identityGetSelfGroupAccessSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO[]>,
  ) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, identityGetSelfGroupAccess: true },
    loading: { ...state.loading, identityGetSelfGroupAccess: null },
    error: { ...(state.error || {}), identityGetSelfGroupAccess: null },
   };
  },
  identityGetSelfGroupAccessFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetSelfGroupAccess: null },
    loading: { ...state.loading, identityGetSelfGroupAccess: null },
    error: {
     ...(state.error || {}),
     identityGetSelfGroupAccess: action.payload,
    },
   };
  },
  identityGetSelfRolesAccessStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityGetSelfRolesAccess: true },
   };
  },
  identityGetSelfRolesAccessSuccess(state, action: PayloadAction<RolesDTO[]>) {
   return {
    ...state,
    roles: action.payload,
    success: { ...state.success, identityGetSelfRolesAccess: true },
    loading: { ...state.loading, identityGetSelfRolesAccess: null },
    error: { ...(state.error || {}), identityGetSelfRolesAccess: null },
   };
  },
  identityGetSelfRolesAccessFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetSelfRolesAccess: null },
    loading: { ...state.loading, identityGetSelfRolesAccess: null },
    error: {
     ...(state.error || {}),
     identityGetSelfRolesAccess: action.payload,
    },
   };
  },
  identityGetSelfGroupsAccessStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityGetSelfGroupsAccess: true },
   };
  },
  identityGetSelfGroupsAccessSuccess(
   state,
   action: PayloadAction<AccessGroupsDTO[]>,
  ) {
   return {
    ...state,
    accessGroups: action.payload,
    success: { ...state.success, identityGetSelfGroupsAccess: true },
    loading: { ...state.loading, identityGetSelfGroupsAccess: null },
    error: { ...(state.error || {}), identityGetSelfGroupsAccess: null },
   };
  },
  identityGetSelfGroupsAccessFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetSelfGroupsAccess: null },
    loading: { ...state.loading, identityGetSelfGroupsAccess: null },
    error: {
     ...(state.error || {}),
     identityGetSelfGroupsAccess: action.payload,
    },
   };
  },
  identityGetItemGroupsStart(state, action: PayloadAction<FilterDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityGetItemGroups: true },
   };
  },
  identityGetItemGroupsSuccess(state, action: PayloadAction<GroupDTO[]>) {
   return {
    ...state,
    groups: action.payload,
    success: { ...state.success, identityGetItemGroups: true },
    loading: { ...state.loading, identityGetItemGroups: null },
    error: { ...(state.error || {}), identityGetItemGroups: null },
   };
  },
  identityGetItemGroupsFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetItemGroups: null },
    loading: { ...state.loading, identityGetItemGroups: null },
    error: { ...(state.error || {}), identityGetItemGroups: action.payload },
   };
  },
  identityGetItemGroupStart(
   state,
   action: PayloadAction<{ id: number; fields?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGetItemGroup: true },
   };
  },
  identityGetItemGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, identityGetItemGroup: true },
    loading: { ...state.loading, identityGetItemGroup: null },
    error: { ...(state.error || {}), identityGetItemGroup: null },
   };
  },
  identityGetItemGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGetItemGroup: null },
    loading: { ...state.loading, identityGetItemGroup: null },
    error: { ...(state.error || {}), identityGetItemGroup: action.payload },
   };
  },
  identityCreateItemGroupStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, identityCreateItemGroup: true },
   };
  },
  identityCreateItemGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, identityCreateItemGroup: true },
    loading: { ...state.loading, identityCreateItemGroup: null },
    error: { ...(state.error || {}), identityCreateItemGroup: null },
   };
  },
  identityCreateItemGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityCreateItemGroup: null },
    loading: { ...state.loading, identityCreateItemGroup: null },
    error: { ...(state.error || {}), identityCreateItemGroup: action.payload },
   };
  },
  identityUpdateItemGroupStart(
   state,
   action: PayloadAction<{ id: number; data: any }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityUpdateItemGroup: true },
   };
  },
  identityUpdateItemGroupSuccess(state, action: PayloadAction<GroupDTO>) {
   return {
    ...state,
    group: action.payload,
    success: { ...state.success, identityUpdateItemGroup: true },
    loading: { ...state.loading, identityUpdateItemGroup: null },
    error: { ...(state.error || {}), identityUpdateItemGroup: null },
   };
  },
  identityUpdateItemGroupFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityUpdateItemGroup: null },
    loading: { ...state.loading, identityUpdateItemGroup: null },
    error: { ...(state.error || {}), identityUpdateItemGroup: action.payload },
   };
  },
  identityGetItemStart(state, action: PayloadAction<{ id: number }>) {
   return {
    ...state,
    loading: { ...state.loading, getItem: true },
   };
  },
  identityGetItemSuccess(state, action: PayloadAction<UsersDTO>) {
   return {
    ...state,
    item: action.payload,
    success: { ...state.success, getItem: true },
    loading: { ...state.loading, getItem: null },
    error: { ...(state.error || {}), getItem: null },
   };
  },
  identityGetItemFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, getItem: null },
    loading: { ...state.loading, getItem: null },
    error: { ...(state.error || {}), getItem: action.payload },
   };
  },
  identityGenerateApiKeyStart(
   state,
   action: PayloadAction<{ userId: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGenerateApiKey: true },
   };
  },
  identityGenerateApiKeySuccess(
   state,
   action: PayloadAction<{ apiKey: string }>,
  ) {
   return {
    ...state,
    success: { ...state.success, identityGenerateApiKey: true },
    loading: { ...state.loading, identityGenerateApiKey: null },
    error: { ...(state.error || {}), identityGenerateApiKey: null },
   };
  },
  identityGenerateApiKeyFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGenerateApiKey: null },
    loading: { ...state.loading, identityGenerateApiKey: null },
    error: { ...(state.error || {}), identityGenerateApiKey: action.payload },
   };
  },
 },
});
export const {
 getTableStart,
 getTableSuccess,
 getTableFailure,
 getTableFinish,
 getItemStart,
 getItemSuccess,
 getItemFailure,
 createItemStart,
 createItemSuccess,
 createItemFailure,
 createItemFinish,
 updateItemStart,
 updateItemSuccess,
 updateItemFailure,
 updateItemFinish,
 deleteItemStart,
 deleteItemSuccess,
 deleteItemFailure,
 deleteItemFinish,
 getTableStructureStart,
 getTableStructureSuccess,
 getTableStructureFailure,
 getItemDocumentsStart,
 getItemDocumentsSuccess,
 getItemDocumentsFailure,
 uploadItemDocumentsStart,
 uploadItemDocumentsSuccess,
 uploadItemDocumentsFailure,
 uploadItemDocumentsFinish,
 deleteItemDocumentStart,
 deleteItemDocumentSuccess,
 deleteItemDocumentFailure,
 deleteItemDocumentFinish,
 getTableExcelStart,
 getTableExcelSuccess,
 getTableExcelFailure,
 getTableExcelFinish,
 identityLoginStart,
 identityLoginSuccess,
 identityLoginFailure,
 identityLogoutStart,
 identityLogoutSuccess,
 identityLogoutFailure,
 identityRefreshTokenStart,
 identityRefreshTokenSuccess,
 identityRefreshTokenFailure,
 identityMicrosoftStart,
 identityMicrosoftSuccess,
 identityMicrosoftFailure,
 identityMicrosoftCallbackStart,
 identityMicrosoftCallbackSuccess,
 identityMicrosoftCallbackFailure,
 identityGoogleStart,
 identityGoogleSuccess,
 identityGoogleFailure,
 identityGoogleCallbackStart,
 identityGoogleCallbackSuccess,
 identityGoogleCallbackFailure,
 identityVerify2FAStart,
 identityVerify2FASuccess,
 identityVerify2FAFailure,
 identityGenerate2FACodeStart,
 identityGenerate2FACodeSuccess,
 identityGenerate2FACodeFailure,
 identitySetVerificationStart,
 identitySetVerificationSuccess,
 identitySetVerificationFailure,
 identityConfirmVerificationStart,
 identityConfirmVerificationSuccess,
 identityConfirmVerificationFailure,
 identitySetTotpStart,
 identitySetTotpSuccess,
 identitySetTotpFailure,
 identityVerifyTotpStart,
 identityVerifyTotpSuccess,
 identityVerifyTotpFailure,
 identityGetSelfStart,
 identityGetSelfSuccess,
 identityGetSelfFailure,
 identityGetSelfFinish,
 identityUpdateSelfStart,
 identityUpdateSelfSuccess,
 identityUpdateSelfFailure,
 identityUpdateSelfFinish,
 identityGetRolesStart,
 identityGetRolesSuccess,
 identityGetRolesFailure,
 identityGetItemRolesStart,
 identityGetItemRolesSuccess,
 identityGetItemRolesFailure,
 identityGetItemGroupRolesStart,
 identityGetItemGroupRolesSuccess,
 identityGetItemGroupRolesFailure,
 identityAddRoleToItemGroupStart,
 identityAddRoleToItemGroupSuccess,
 identityAddRoleToItemGroupFailure,
 identityCreateRoleStart,
 identityCreateRoleSuccess,
 identityCreateRoleFailure,
 identityCreateAccessGroupStart,
 identityCreateAccessGroupSuccess,
 identityCreateAccessGroupFailure,
 identityDeleteRoleStart,
 identityDeleteRoleSuccess,
 identityDeleteRoleFailure,
 identityRemoveRoleFromGroupStart,
 identityRemoveRoleFromGroupSuccess,
 identityRemoveRoleFromGroupFailure,
 identityRevokeTokenStart,
 identityRevokeTokenSuccess,
 identityRevokeTokenFailure,
 identityRevokeAllTokensStart,
 identityRevokeAllTokensSuccess,
 identityRevokeAllTokensFailure,
 identityGetSelfGroupAccessStart,
 identityGetSelfGroupAccessSuccess,
 identityGetSelfGroupAccessFailure,
 identityGetSelfRolesAccessStart,
 identityGetSelfRolesAccessSuccess,
 identityGetSelfRolesAccessFailure,
 identityGetSelfGroupsAccessStart,
 identityGetSelfGroupsAccessSuccess,
 identityGetSelfGroupsAccessFailure,
 identityGetItemGroupsStart,
 identityGetItemGroupsSuccess,
 identityGetItemGroupsFailure,
 identityGetItemGroupStart,
 identityGetItemGroupSuccess,
 identityGetItemGroupFailure,
 identityCreateItemGroupStart,
 identityCreateItemGroupSuccess,
 identityCreateItemGroupFailure,
 identityUpdateItemGroupStart,
 identityUpdateItemGroupSuccess,
 identityUpdateItemGroupFailure,
 identityGetItemStart,
 identityGetItemSuccess,
 identityGetItemFailure,
 identityGenerateApiKeyStart,
 identityGenerateApiKeySuccess,
 identityGenerateApiKeyFailure,
} = identitySlice.actions;
export default identitySlice;

